// src/hooks/useInteractionState.ts
//
// Generic interaction state for list+chart surfaces.
// Instantiate once per entity type (signals, trades, alerts).
// Both the chart and the sidebar consume the same instance via context.

import { useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InteractionState<TId = string> {
    /** Id of the item the pointer is currently over (either surface). */
    hoveredId: TId | null;
    /** Id of the item that was clicked and should stay visually selected. */
    selectedId: TId | null;
    /**
     * When set, only items in this set are "active" (chart renders them at
     * full opacity; sidebar list is already filtered to them).
     * null = no filter, everything is active.
     */
    visibleIds: Set<TId> | null;
}

export interface InteractionActions<TId = string> {
    hover: (id: TId | null) => void;
    select: (id: TId | null) => void;
    setVisibleIds: (ids: Set<TId> | null) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useInteractionState<TId = string>(): [
    InteractionState<TId>,
    InteractionActions<TId>,
] {
    const [state, setState] = useState<InteractionState<TId>>({
        hoveredId: null,
        selectedId: null,
        visibleIds: null,
    });

    const hover = useCallback((id: TId | null) =>
        setState(s => s.hoveredId === id ? s : { ...s, hoveredId: id }),
        []);

    const select = useCallback((id: TId | null) =>
        setState(s => s.selectedId === id ? s : { ...s, selectedId: id }),
        []);

    const setVisibleIds = useCallback((ids: Set<TId> | null) =>
        setState(s => ({ ...s, visibleIds: ids })),
        []);

    return [state, { hover, select, setVisibleIds }];
}